import { useEffect, useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import { Select } from '../../../shared/components/Select';
import Button from '../../../shared/components/Button';
import {
  useCharterAvailability,
  useCharterProvider,
  useSetAvailability,
  useUpdateCancellationPolicy,
  useUpdateGatewayState,
  useUpdateProviderProfile,
} from '../hooks/useCharter';
import { CharterHeader, LoadingState, SelectMerchantPrompt } from '../components/CharterPage';
import { GatewayStateBadge } from '../components/CharterStatusBadge';
import { DAY_OF_WEEK_LABELS, WAT_TIMEZONE } from '../components/charterFormat';
import { GATEWAY_STATE_LABELS, GATEWAY_STATES, type AvailabilityWindow, type GatewayState } from '../types';

const GATEWAY_OPTIONS = GATEWAY_STATES.map((s) => ({ value: s, title: GATEWAY_STATE_LABELS[s] }));

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <h2 className="text-[16px] font-semibold text-foreground">{title}</h2>
      {description && <p className="text-[13px] text-muted-foreground mt-0.5 mb-4">{description}</p>}
      <div className={description ? '' : 'mt-4'}>{children}</div>
    </div>
  );
}

export function CharterSettingsPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const providerQuery = useCharterProvider(selectedTenantId);
  const availabilityQuery = useCharterAvailability(selectedTenantId);
  const updateProfile = useUpdateProviderProfile();
  const updateGateway = useUpdateGatewayState();
  const updatePolicy = useUpdateCancellationPolicy();
  const setAvailability = useSetAvailability();

  const provider = providerQuery.data;

  // Profile form
  const [displayName, setDisplayName] = useState('');
  const [tagline, setTagline] = useState('');
  const [bio, setBio] = useState('');
  const [timezone, setTimezone] = useState(WAT_TIMEZONE);

  // Cancellation policy form
  const [policyText, setPolicyText] = useState('');
  const [cancelHours, setCancelHours] = useState('');
  const [rescheduleHours, setRescheduleHours] = useState('');

  // Availability: one editable row per day-of-week.
  const [windows, setWindows] = useState<Record<number, { enabled: boolean; opensAt: string; closesAt: string }>>({});

  useEffect(() => {
    if (!provider) return;
    setDisplayName(provider.branding?.displayName ?? '');
    setTagline(provider.branding?.tagline ?? '');
    setBio(provider.branding?.bio ?? '');
    setTimezone(provider.timezone ?? WAT_TIMEZONE);
    setPolicyText(provider.cancellationPolicy?.policyText ?? '');
    setCancelHours(
      provider.cancellationPolicy?.cancellationNoticeHours != null
        ? String(provider.cancellationPolicy.cancellationNoticeHours)
        : '',
    );
    setRescheduleHours(
      provider.cancellationPolicy?.rescheduleNoticeHours != null
        ? String(provider.cancellationPolicy.rescheduleNoticeHours)
        : '',
    );
  }, [provider]);

  useEffect(() => {
    const map: Record<number, { enabled: boolean; opensAt: string; closesAt: string }> = {};
    for (let day = 0; day < 7; day++) {
      const existing = availabilityQuery.data?.windows.find((w) => w.dayOfWeek === day);
      map[day] = {
        enabled: !!existing,
        opensAt: existing?.opensAt ?? '09:00',
        closesAt: existing?.closesAt ?? '17:00',
      };
    }
    setWindows(map);
  }, [availabilityQuery.data]);

  const saveProfile = () =>
    updateProfile.mutate({ displayName, tagline, bio, timezone });

  const saveGateway = (state: GatewayState) => updateGateway.mutate({ state });

  const savePolicy = () =>
    updatePolicy.mutate({
      policyText: policyText || undefined,
      cancellationNoticeHours: cancelHours.trim() === '' ? undefined : Number(cancelHours),
      rescheduleNoticeHours: rescheduleHours.trim() === '' ? undefined : Number(rescheduleHours),
    });

  const saveAvailability = () => {
    const list: AvailabilityWindow[] = Object.entries(windows)
      .filter(([, w]) => w.enabled)
      .map(([day, w]) => ({ dayOfWeek: Number(day), opensAt: w.opensAt, closesAt: w.closesAt }));
    setAvailability.mutate({ timezone, windows: list });
  };

  const setDay = (day: number, patch: Partial<{ enabled: boolean; opensAt: string; closesAt: string }>) =>
    setWindows((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));

  if (!selectedTenantId) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        <CharterHeader title="Charter · Settings" subtitle="Provider profile, capacity gateway, cancellation policy and availability." />
        <SelectMerchantPrompt what="their Charter provider settings" />
      </div>
    );
  }

  if (providerQuery.isLoading) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        <CharterHeader title="Charter · Settings" />
        <LoadingState label="Loading provider settings…" />
      </div>
    );
  }

  return (
    <div className="py-6 max-w-4xl mx-auto px-2 space-y-5">
      <CharterHeader
        title="Charter · Settings"
        subtitle="Provider profile, capacity gateway, cancellation policy and availability (WAT)."
      />

      <Section title="Provider profile">
        <div className="space-y-4">
          <Input label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={!canManage} />
          <Input label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} disabled={!canManage} />
          <TextArea label="Bio" value={bio} onChange={(e) => setBio(e.target.value)} disabled={!canManage} />
          <Input label="Timezone (IANA)" value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={!canManage} />
          {canManage && (
            <div className="flex justify-end">
              <Button size="sm" className="h-[40px]" onClick={saveProfile} disabled={updateProfile.isPending} loading={updateProfile.isPending}>
                Save profile
              </Button>
            </div>
          )}
        </div>
      </Section>

      <Section title="Capacity gateway" description="Whether the storefront takes bookings, offers a waitlist, or is closed.">
        <div className="flex items-center gap-4">
          <GatewayStateBadge state={provider?.gatewayState ?? 'open'} />
          {canManage && (
            <div className="max-w-[240px] w-full">
              <Select
                options={GATEWAY_OPTIONS}
                value={provider?.gatewayState ?? 'open'}
                onChange={(v) => saveGateway(v as GatewayState)}
              />
            </div>
          )}
        </div>
      </Section>

      <Section title="Cancellation & reschedule policy">
        <div className="space-y-4">
          <TextArea label="Policy text" value={policyText} onChange={(e) => setPolicyText(e.target.value)} disabled={!canManage} />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Cancellation notice (hours)"
              type="number"
              min={0}
              value={cancelHours}
              onChange={(e) => setCancelHours(e.target.value)}
              disabled={!canManage}
            />
            <Input
              label="Reschedule notice (hours)"
              type="number"
              min={0}
              value={rescheduleHours}
              onChange={(e) => setRescheduleHours(e.target.value)}
              disabled={!canManage}
            />
          </div>
          {canManage && (
            <div className="flex justify-end">
              <Button size="sm" className="h-[40px]" onClick={savePolicy} disabled={updatePolicy.isPending} loading={updatePolicy.isPending}>
                Save policy
              </Button>
            </div>
          )}
        </div>
      </Section>

      <Section title="Availability windows" description="Recurring day-of-week opening hours. Slot capacity per window is managed downstream (default 1 concurrent booking).">
        {availabilityQuery.isLoading ? (
          <LoadingState label="Loading availability…" />
        ) : (
          <div className="space-y-2">
            {DAY_OF_WEEK_LABELS.map((label, day) => {
              const w = windows[day] ?? { enabled: false, opensAt: '09:00', closesAt: '17:00' };
              return (
                <div key={day} className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 min-w-[130px]">
                    <input
                      type="checkbox"
                      checked={w.enabled}
                      onChange={(e) => setDay(day, { enabled: e.target.checked })}
                      disabled={!canManage}
                      className="h-4 w-4 accent-[#28282A]"
                    />
                    <span className="text-sm text-foreground">{label}</span>
                  </label>
                  <input
                    type="time"
                    value={w.opensAt}
                    onChange={(e) => setDay(day, { opensAt: e.target.value })}
                    disabled={!canManage || !w.enabled}
                    className="border border-[#D9D9D9] bg-muted text-foreground rounded-[8px] px-2 py-1 text-sm disabled:opacity-50"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <input
                    type="time"
                    value={w.closesAt}
                    onChange={(e) => setDay(day, { closesAt: e.target.value })}
                    disabled={!canManage || !w.enabled}
                    className="border border-[#D9D9D9] bg-muted text-foreground rounded-[8px] px-2 py-1 text-sm disabled:opacity-50"
                  />
                </div>
              );
            })}
            {canManage && (
              <div className="flex justify-end pt-2">
                <Button size="sm" className="h-[40px]" onClick={saveAvailability} disabled={setAvailability.isPending} loading={setAvailability.isPending}>
                  Save availability
                </Button>
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}
